export type AvailabilityStatus = 'online' | 'busy' | 'away' | 'offline' | 'dnd';

export interface UserAvailability {
  userId: string;
  userName: string;
  email: string;
  status: AvailabilityStatus;
  statusText?: string;
  currentActivity?: string;
  lastSeen: string;
}

export interface PresenceHeartbeat {
  userId: string;
  timestamp: string;
}

const STORAGE_KEY = 'kontora_user_availability_v1';
const HEARTBEAT_KEY = 'kontora_presence_heartbeats_v1';
const HEARTBEAT_TIMEOUT_SEC = 120;

export function loadAvailabilities(): UserAvailability[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as UserAvailability[];
  } catch {
    return [];
  }
}

export function saveAvailabilities(items: UserAvailability[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

export function setMyAvailability(
  userId: string,
  userName: string,
  email: string,
  status: AvailabilityStatus,
  statusText?: string,
  currentActivity?: string,
): UserAvailability {
  const all = loadAvailabilities();
  const idx = all.findIndex((u) => u.userId === userId);
  const updated: UserAvailability = {
    userId,
    userName,
    email,
    status,
    statusText,
    currentActivity,
    lastSeen: new Date().toISOString(),
  };
  if (idx === -1) {
    all.push(updated);
  } else {
    all[idx] = updated;
  }
  saveAvailabilities(all);
  recordHeartbeat(userId);
  return updated;
}

export function getMyAvailability(userId: string): UserAvailability | undefined {
  return loadAvailabilities().find((u) => u.userId === userId);
}

export function recordHeartbeat(userId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(HEARTBEAT_KEY) ?? '{}';
    const all = JSON.parse(raw) as Record<string, PresenceHeartbeat>;
    all[userId] = { userId, timestamp: new Date().toISOString() };
    localStorage.setItem(HEARTBEAT_KEY, JSON.stringify(all));
  } catch {}
}

export function isOnline(lastSeen: string): boolean {
  const diffSec = (Date.now() - new Date(lastSeen).getTime()) / 1000;
  return diffSec < HEARTBEAT_TIMEOUT_SEC;
}

export function getOnlineUsers(items: UserAvailability[]): UserAvailability[] {
  return items.filter((u) => isOnline(u.lastSeen) && u.status !== 'offline');
}

export function getUserById(userId: string, items: UserAvailability[]): UserAvailability | undefined {
  return items.find((u) => u.userId === userId);
}

export function statusLabel(status: AvailabilityStatus): string {
  const labels: Record<AvailabilityStatus, string> = {
    online: 'Online',
    busy: 'Beschäftigt',
    away: 'Abwesend',
    offline: 'Offline',
    dnd: 'Nicht stören',
  };
  return labels[status];
}

export function statusColorVar(status: AvailabilityStatus): string {
  const map: Record<AvailabilityStatus, string> = {
    online: 'var(--k-color-success-strong)',
    busy: 'var(--k-color-warning-strong)',
    away: 'var(--k-color-text-muted)',
    offline: 'var(--k-color-text-muted)',
    dnd: 'var(--k-color-danger-strong)',
  };
  return map[status];
}

export function statusEmoji(status: AvailabilityStatus): string {
  const map: Record<AvailabilityStatus, string> = {
    online: '🟢',
    busy: '🟡',
    away: '🟠',
    offline: '⚫',
    dnd: '🔴',
  };
  return map[status];
}

export function relativeLastSeen(lastSeen: string): string {
  const diffSec = Math.floor((Date.now() - new Date(lastSeen).getTime()) / 1000);
  if (diffSec < 60) return 'gerade eben';
  if (diffSec < 3600) return `vor ${Math.floor(diffSec / 60)} Min.`;
  if (diffSec < 86400) return `vor ${Math.floor(diffSec / 3600)} Std.`;
  return `vor ${Math.floor(diffSec / 86400)} Tagen`;
}

export const DEMO_TEAM: UserAvailability[] = [
  {
    userId: 'usr-1',
    userName: 'Anna Schmidt',
    email: 'anna@kontora.de',
    status: 'online',
    lastSeen: new Date(Date.now() - 30 * 1000).toISOString(),
    currentActivity: 'Rechnung RE-001 erstellen',
  },
  {
    userId: 'usr-2',
    userName: 'Max Bauer',
    email: 'max@kontora.de',
    status: 'busy',
    lastSeen: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    statusText: 'Im Meeting bis 15:30',
    currentActivity: 'Monatsabschluss',
  },
  {
    userId: 'usr-3',
    userName: 'Lisa Müller',
    email: 'lisa@kontora.de',
    status: 'away',
    lastSeen: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
  },
  {
    userId: 'usr-4',
    userName: 'Tom Weber',
    email: 'tom@kontora.de',
    status: 'dnd',
    lastSeen: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    statusText: 'Fokus-Zeit bis 16:00',
  },
  {
    userId: 'usr-5',
    userName: 'Sarah Klein',
    email: 'sarah@kontora.de',
    status: 'offline',
    lastSeen: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
  },
];
